import { useState } from 'react';
import { cardsData, rankingData, indexData } from '@/mocks/homeData';

function CardImage({ src, alt, cardId }: { src: string; alt: string; cardId: number }) {
  const [error, setError] = useState(false);
  if (error) return null;
  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-lg">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        onError={() => setError(true)}
        loading="lazy"
      />
      <div className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm rounded-md text-xs font-medium text-foreground-700">
        #{cardId}
      </div>
    </div>
  );
}

export default function SectionsOne() {
  return (
    <>
      <section id="cards" className="py-14 md:py-20 px-4 md:px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              经典影片精选
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              从港片黄金时代到华语文艺巅峰，精选五部不可错过的8090年代必看经典
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {cardsData.map((card) => (
              <article
                key={card.id}
                className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <CardImage src={card.image} alt={card.title} cardId={card.id} />
                <div className="p-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5 group-hover:text-primary-600 transition-colors">
                    {card.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs text-primary-600 font-medium">{card.year}</span>
                    <span className="text-xs text-foreground-400">|</span>
                    <span className="text-xs text-foreground-500">{card.genre}</span>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-3">
                    {card.desc}
                  </p>
                  <div className="mt-3 pt-3 border-t border-background-200">
                    <span className="text-xs text-secondary-600">
                      <i className="ri-user-line mr-1"></i>{card.director}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="ranking" className="py-14 md:py-20 px-4 md:px-6 bg-background-100 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              热度排行
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              近期最受8090影迷关注的经典影片，看看大家在重温哪些老电影
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {rankingData.map((item) => (
              <div
                key={item.rank}
                className="flex items-center gap-4 py-3.5 px-4 border-b border-background-200 last:border-b-0 hover:bg-background-50 rounded-lg transition-colors cursor-pointer group"
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg font-heading font-bold text-sm shrink-0 ${
                  item.rank <= 3
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-200 text-foreground-600'
                }`}>
                  {item.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                      {item.title}
                    </span>
                    <span className="text-xs text-foreground-400 shrink-0">{item.year}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-medium text-accent-600">{item.hot.toLocaleString()}</span>
                  <span className="text-xs text-foreground-400">热度</span>
                  <i className={`text-sm ${
                    item.trend === 'up' ? 'ri-arrow-up-line text-accent-500' :
                    item.trend === 'down' ? 'ri-arrow-down-line text-foreground-400' :
                    'ri-subtract-line text-foreground-400'
                  }`}></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="index" className="py-14 md:py-20 px-4 md:px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              电影专题
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              按类型精心划分的经典电影合集，总有一个专题让你流连忘返
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
            {indexData.map((item) => (
              <a
                key={item.id}
                href="#index"
                className="group flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200 hover:border-secondary-300 hover:bg-secondary-50/50 transition-all duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-secondary-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                  <span className="inline-block mt-1.5 text-xs text-secondary-500 font-medium">
                    {item.count}部
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}